import { useMemo, useCallback, type ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useQueryClient } from '@tanstack/react-query';
import {
  appendCheckoutStartCorrelation,
  FunnelProvider,
  createStorefrontBffSemanticPorts,
  type SemanticPortContext,
} from '@template-project/storefront-funnel';
import { useCartContext } from '@/lib/context/cart-context';
import { useToast } from '@/lib/context/toast-context';
import { layoutFunnelAnalytics } from '@/lib/funnel-analytics-bridge';
import { env } from '@/lib/utils/env';
import { cartService, productService } from '@/infrastructure/config/services.config';
import type { CartEntity } from '@/domain/entities/cart.entity';

const isSsr = import.meta.env.SSR === true;

export function FunnelRuntimeProvider({ children }: { children: ReactNode }) {
  const { cartToken } = useCartContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const showToast = useCallback(
    (input: {
      message: string;
      variant?: 'success' | 'error' | 'info' | 'warning';
      duration?: number;
    }) => {
      const payload =
        input.duration !== undefined
          ? { message: input.message, duration: input.duration }
          : input.message;
      switch (input.variant) {
        case 'success':
          toast.success(payload);
          break;
        case 'error':
          toast.error(payload);
          break;
        case 'warning':
          toast.warning(payload);
          break;
        case 'info':
        default:
          toast.info(payload);
          break;
      }
    },
    [toast],
  );
  const semanticPorts = useMemo(
    () => ({
      ...createStorefrontBffSemanticPorts({
        apiBaseUrl: env.STOREFRONT_BFF_URL,
        tenantId: env.FUNNELS_TENANT_ID,
        startCheckout: ({ cartId }, context) => {
          const resolvedCartId = cartId ?? cartToken;
          if (!resolvedCartId) {
            throw new Error('start_checkout requires an active cart');
          }
          if (typeof window === 'undefined') {
            throw new Error('start_checkout is unavailable during SSR');
          }
          const checkoutUrl = `${env.CHECKOUT_URL.replace(/\/+$/, '')}/checkout/${encodeURIComponent(resolvedCartId)}`;
          window.location.assign(appendCheckoutStartCorrelation(checkoutUrl, context.eventId));
          return { cartId: resolvedCartId };
        },
      }),
      removeFromCart: async (
        {
          productId,
          variantId,
          lineId,
        }: {
          productId?: string;
          variantId?: string;
          lineId?: string;
        },
        context: SemanticPortContext,
      ) => {
        if (!cartToken) {
          showToast({
            variant: 'error',
            message: 'No active cart to remove from',
          });
          throw new Error('remove_from_cart requires an active cart');
        }
        let cachedCart = queryClient.getQueryData<CartEntity | null>(['cart', cartToken]);
        // React Query may not have hydrated yet on /cart; fetch so Automatic
        // identity can resolve from the primary line.
        if (!cachedCart?.items?.length) {
          cachedCart = await cartService.getCart(cartToken);
          if (cachedCart) {
            queryClient.setQueryData<CartEntity | null>(['cart', cartToken], cachedCart);
          }
        }
        const automaticLine =
          !productId?.trim() || !variantId?.trim()
            ? cachedCart?.items[0]
            : undefined;
        const resolvedProductId = productId?.trim() || automaticLine?.product_id || '';
        const resolvedVariantId = variantId?.trim() || automaticLine?.variant_id || '';
        const resolvedLineId =
          lineId?.trim() ||
          (automaticLine ? automaticLine.id : undefined);
        if (!resolvedProductId || !resolvedVariantId) {
          showToast({
            variant: 'error',
            message: 'Cart line identity missing — add an item first',
          });
          throw new Error('remove_from_cart requires product and variant identity');
        }
        const cachedLine = resolvedLineId
          ? cachedCart?.items.find(
              (item) =>
                item.id === resolvedLineId &&
                item.product_id === resolvedProductId &&
                item.variant_id === resolvedVariantId,
            )
          : undefined;
        const authoritativeLineId = cachedLine?.line_id ?? (cachedLine ? undefined : resolvedLineId);
        try {
          const cart = await cartService.removeCartItem(
            cartToken,
            resolvedProductId,
            resolvedVariantId,
            authoritativeLineId,
            context.eventId ?? context.correlationId ?? uuidv4(),
          );
          queryClient.setQueryData<CartEntity | null>(['cart', cartToken], cart);
          await queryClient.invalidateQueries({ queryKey: ['cart', cartToken] });
          showToast({
            variant: 'success',
            message: 'Removed from cart',
          });
          return {
            resultParams: {
              cartId: cartToken,
              productId: resolvedProductId,
              variantId: resolvedVariantId,
              ...(resolvedLineId ? { lineId: resolvedLineId } : {}),
            },
          };
        } catch (error) {
          showToast({
            variant: 'error',
            message: 'Could not remove cart line',
          });
          throw error;
        }
      },
    }),
    [cartToken, queryClient, showToast]
  );

  return (
    <FunnelProvider
      config={{
        apiBaseUrl: env.STOREFRONT_BFF_URL,
        tenantId: env.FUNNELS_TENANT_ID,
        checkoutMiddlewareUrl: env.CHECKOUT_MIDDLEWARE_URL,
      }}
      runtimeVersion={env.RUNTIME_VERSION}
      analytics={isSsr ? undefined : layoutFunnelAnalytics}
      semanticPorts={semanticPorts}
      showToast={isSsr ? undefined : showToast}
      resolveProductImage={
        isSsr
          ? undefined
          : async (productId) => {
              const product = await productService.getProductById(productId).catch(() => null);
              return product?.images?.[0]?.src;
            }
      }
    >
      {children}
    </FunnelProvider>
  );
}
